import { Form, FormGroup, Select, TextArea, TextInput } from '@ksyos/design-system';
import { useEffect, useMemo, useState } from 'react';
import type { useFormData } from '@frontend/contexts/support/application/hooks/useFormData';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IDeviceType } from '../deviceAccessoryTypes/DeviceAccessoryTypesList';
import type { IDevice } from './DeviceDetails';
import type { DeviceInformationCardEditFormData } from './DeviceInformationCard';

type DeviceInformationCardEditBodyProps = {
    device: Pick<IDevice, 'deviceType'>;
    formData: ReturnType<typeof useFormData<DeviceInformationCardEditFormData>>;
};

export function DeviceInformationCardEditBody(props: Readonly<DeviceInformationCardEditBodyProps>) {
    const translate = getSelectedLanguage();

    const {
        device,
        formData: { formData, update, errors },
    } = props;

    const abortSignal = useAbortSignal();
    const [deviceTypesList, setDeviceTypesList] = useState<IDeviceType[]>([{ ...device.deviceType }]);
    const deviceTypeOptionsList = useMemo(getDeviceTypesList, [deviceTypesList]);

    useEffect(() => {
        loadDeviceTypes();
    }, []);

    return (
        <Form submitOnEnter={false}>
            <FormGroup
                hasErrors={errors.has('deviceTypeId')}
                isRequired={true}
                label={translate.devices.type}
            >
                <Select
                    error={getValidationErrorTranslation(errors.get('deviceTypeId'))}
                    onChange={(value) => {
                        update({ deviceTypeId: value?.value ?? null });
                    }}
                    options={deviceTypeOptionsList}
                    placeholder={translate.devices.deviceTypePlaceholder}
                    value={deviceTypeOptionsList.find((item) => item.value === formData.deviceTypeId)}
                />
            </FormGroup>

            <FormGroup
                hasErrors={errors.has('externalId')}
                isRequired={true}
                label={translate.devices.serialNumber}
            >
                <TextInput
                    error={getValidationErrorTranslation(errors.get('externalId'))}
                    onChange={(event) => {
                        update({ externalId: event.currentTarget.value });
                    }}
                    placeholder={translate.devices.serialNumberPlaceholder}
                    value={formData.externalId ?? ''}
                    variant="outline"
                />
            </FormGroup>

            <FormGroup
                hasErrors={errors.has('notes')}
                label={translate.devices.notes}
            >
                <TextArea
                    error={getValidationErrorTranslation(errors.get('notes'))}
                    onChange={(event) => {
                        update({ notes: event.currentTarget.value });
                    }}
                    placeholder={`${translate.devices.notes}...`}
                    value={formData.notes ?? ''}
                    variant="outline"
                />
            </FormGroup>
        </Form>
    );

    function getDeviceTypesList() {
        return deviceTypesList.map((item: IDeviceType) => ({
            label: item.name,
            value: item.id,
        }));
    }

    async function loadDeviceTypes() {
        const result = await fetchJson({ method: 'GET', signal: abortSignal, url: '/api/deviceTypes' });

        setDeviceTypesList(result.json.items);
    }
}
